import React from 'react'

import { Alert } from 'react-bootstrap'
import {
  MultiSelectField, Checkbox, MultiLineField, DateField, Label, BooleanRadioButtonField, IField, validateFieldsEx, yesNoDataSource,
} from '../../../../../shared/fields'
import { ApplicationModel } from '../../../../../models/ApplicationModel'
import { Option } from '../../../../../shared/fields/Options'
import { getMarketCountries } from '../../../../../shared/Utility'
import EMDGAccordion from '../../../../../shared/EMDGAccordion'
import { GRANT_GUIDELINES_URL } from '../../..'

export interface Props {
    model: ApplicationModel
    onChange: { (changes: Partial<ApplicationModel>, callback?: { (): any }): any }
    isSelected: boolean
    tierSelected: boolean
    preview?: boolean
    markets: Option<string>[]
    marketsRegions: string[]
}

export interface State {

    filteredMarkets: Option<string>[]
    validationError: boolean
}

export default class Tier1 extends React.Component<Props, State> implements IField {
  hiddenCheckboxForValidation: Checkbox
  hasEverExported: BooleanRadioButtonField
  hasExportReadiness: BooleanRadioButtonField
  marketingSkillsInForeignCountry: BooleanRadioButtonField
  exportReadinessEvidence: Checkbox
  exportReadinessTrainingCourseDate: DateField
  completedExpoReadinessTestFromAustrade: Checkbox
  hasDesignatedConnectionToEligibleProducts: BooleanRadioButtonField
  designatedConnectionToEligibleProducts: MultiLineField

  constructor(props: Props) {
    super(props)

    const { markets, marketsRegions, model } = this.props

    const filteredMarkets = getMarketCountries(markets, marketsRegions)
    this.setExportReadyFlag(model.abn)

    this.state = { filteredMarkets, validationError: false }
  }

  componentDidUpdate(prevProps: Props) {
    const { model } = this.props
    const { validationError } = this.state

    if (validationError && (model.hasExportReadiness || model.exportReadinessEvidence || (model.completedExpoReadinessTestFromAustrade && !model.hasExportReadiness))
    ) this.setState({ validationError: false })

    if (model.hasDesignatedConnectionToEligibleProducts === true) {
      if (model.designatedConnectionToEligibleProducts === null || model.designatedConnectionToEligibleProducts.match(/^\s*$/)) {
        model.designatedConnectionToEligibleProducts = null
      }
    }

    if (!model.exportReadinessEvidence) {
      model.exportReadinessTrainingCourseDate = null
    }
  }

  setExportReadyFlag = (abn: string) => {
    const url = `/api/Dashboard/IsExportReady?abn=${abn}`
    fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => response.json())
      .then((tf) => {
        console.warn('Is-Export-Ready:', tf)
        const { model } = this.props
        model.hasExportReadiness = tf
        // If API call found export readiness, then set field of Q30 and checkbox from Q30a to true
        if (model.hasExportReadiness) {
          model.marketingSkillsInForeignCountry = true
          model.completedExpoReadinessTestFromAustrade = true
        }
      })
      .catch((error) => console.error('Error:', error))
  }

  validate(callback: { (res: boolean): any }, focus?: boolean) {
    const { model } = this.props

    if (model.hasEverExported === true
            || model.hasDesignatedConnectionToEligibleProducts === false
            || model.marketingSkillsInForeignCountry === false) {
      callback && callback(false) // don't ever continue!

      return
    }
    this.setState({
      validationError: (!(model.hasExportReadiness && model.completedExpoReadinessTestFromAustrade)
                    && (!model.exportReadinessEvidence))
                    || (!model.hasExportReadiness && model.completedExpoReadinessTestFromAustrade),
    })
    validateFieldsEx(
      focus,
      callback,
      this.hiddenCheckboxForValidation,
      this.hasEverExported,
      this.marketingSkillsInForeignCountry,
      this.hasExportReadiness,
      this.exportReadinessTrainingCourseDate,
      this.hasDesignatedConnectionToEligibleProducts,
      this.designatedConnectionToEligibleProducts,
    )
  }

  render() {
    const {
      model, onChange, isSelected, tierSelected, preview,
    } = this.props

    const { validationError } = this.state
    const labelDoYouHaveAppropriateSkills = '30. Do you have the appropriate skills in marketing eligible products in a foreign market (economy)?'
    const labelWhichCompleted = '30.a Which of the following have you successfully completed to demonstrate that you have these skills: *'
    const labelTrainingCompleteDate = '30.b Provide the date that you completed the export readiness training course.'
    const labelDoYouHaveDesignatedConnection = '31. Do you have a designated connection to eligible products?'
    const labelDesignatedConnectionDescription = '31.a Explain how you have a designated connection to your eligible products.'
    const labelHasExportReadiness = '41.b You must answer the following questions?'
    const descriptionHasExportReadiness = ''
    const formElems = (
      <>
        {model.hasEverExported && (
        <Alert className="emdg-page-alerts emdg-page-alerts--error">
          You are
          {' '}
          <b>not </b>
          {' '}
          eligible for a grant with Austrade for this tier.
        </Alert>
        )}

        {model.hasEverExported === false && (
        <Alert className="emdg-page-alerts emdg-page-alerts--info">
          You declare that you have not previously exported.
        </Alert>

        )}
        {(model.hasEverExported === false) && (
        // Q30
        <>
          <BooleanRadioButtonField
            ref={(r) => { this.marketingSkillsInForeignCountry = r }}
            id="marketingSkillsInForeignCountry"
            label={labelDoYouHaveAppropriateSkills}
            value={model.marketingSkillsInForeignCountry}
            onChange={(marketingSkillsInForeignCountry) => onChange({ marketingSkillsInForeignCountry })}
            required={model.hasEverExported === false}
            dataSource={yesNoDataSource}
            disabled={model.hasExportReadiness}
            description={descriptionHasExportReadiness}
            preview={preview}
          />
          {model.marketingSkillsInForeignCountry === false && (
          <Alert className="emdg-page-alerts emdg-page-alerts--error">
            You are
            {' '}
            <b>not</b>
            {' '}
            eligible to apply for a grant for this tier unless you have successfully completed an Export Readiness course. Click
            {' '}
            <a target="_blank" href="/export-readiness">here</a>
            {' '}
            to take the course.
            Grant Agents cannot complete the test on behalf of applicants.
          </Alert>
          )}
          {model.marketingSkillsInForeignCountry === true && (
          // Q30.a
          <>
            <Label
              label={labelWhichCompleted}
              preview={preview}
            />
            <Checkbox
              ref={(r) => { this.hiddenCheckboxForValidation = r }}
              id="hiddenCheckboxForValidation"
              required={(!(model.hasExportReadiness && model.completedExpoReadinessTestFromAustrade)
                    && (!model.exportReadinessEvidence))
                    || (!model.hasExportReadiness && model.completedExpoReadinessTestFromAustrade)}
              onChange={() => { }}
              value={false}
              label=""
              className="hidden-checkbox"
            />
            <Checkbox
              ref={(r) => { this.exportReadinessEvidence = r }}
              id="exportReadinessEvidence"
              label={(
                <>
                  a recognised export readiness training course (from a recognised provider listed on the
                  {' '}
                  <a target="blank" href="https://export.business.gov.au/">Go Global Toolkit</a>
                  {' '}
                  website) and/or
                </>
              )}
              value={model.exportReadinessEvidence}
              onChange={(val) => onChange({ exportReadinessEvidence: val || null })}
              required={false}
              preview={preview}
              validateError={validationError}
            />
            {model.exportReadinessEvidence === true && (
            <Alert className="emdg-page-alerts emdg-page-alerts--warning">
              You must upload evidence to support the completion of your training and provide the date of completion.
              If you have completed the Austrade export readiness test on the portal, your completion will be registered automatically in the application form.
            </Alert>
            )}
            <Checkbox
              ref={(r) => { this.completedExpoReadinessTestFromAustrade = r }}
              id="completedExpoReadinessTestFromAustrade"
              label="the export readiness test available from Austrade."
              value={model.completedExpoReadinessTestFromAustrade}
              onChange={(val) => onChange({ completedExpoReadinessTestFromAustrade: val || null })}
              required={false}
              disabled={model.hasExportReadiness}
              preview={preview}
              validateError={validationError}
            />
            {model.hasExportReadiness !== true && model.completedExpoReadinessTestFromAustrade === true ? (
              <Alert className="emdg-page-alerts emdg-page-alerts--error">
                Our record shows that you have not completed the Austrade export readiness test. To complete the test click
                {' '}
                <a target="_blank" href="/export-readiness">here</a>
                {' '}
                to take the course.
                Grant Agents cannot complete the test on behalf of applicants.
              </Alert>
            ) : null}
            {validationError && (!model.hasExportReadiness && !model.completedExpoReadinessTestFromAustrade) ? (<div className="form-error">This field is required</div>) : null}
            {model.exportReadinessEvidence === true && (
            // Q30.b
            <DateField
              ref={(r) => { this.exportReadinessTrainingCourseDate = r }}
              id="exportReadinessTrainingCourseDate"
              label={labelTrainingCompleteDate}
              value={model.exportReadinessTrainingCourseDate}
              onChange={(exportReadinessTrainingCourseDate) => onChange({ exportReadinessTrainingCourseDate })}
              required
              preview={preview}
              errorMessage={new Date(model.dateBusinessNameChanged) > new Date() ? 'A Future date cannot be entered' : null}
              max={new Date()}
            />
            )}
            { // Q31
              <BooleanRadioButtonField
                ref={(r) => { this.hasDesignatedConnectionToEligibleProducts = r }}
                id="hasDesignatedConnEligProducts"
                label={labelDoYouHaveDesignatedConnection}
                value={model.hasDesignatedConnectionToEligibleProducts}
                onChange={(hasDesignatedConnectionToEligibleProducts) => onChange({ hasDesignatedConnectionToEligibleProducts })}
                required
                dataSource={yesNoDataSource}
                description={(
                  <i>
                    You must have a designated connection to the eligible product for your promotional activity expenses to be eligible.
                    <br />
                    Refer to section 5.3.3 of the
                    {' '}
                    <a target="_blank" href={`${GRANT_GUIDELINES_URL}`} rel="noreferrer">Grant Guidelines</a>
                    {' '}
                    for further information.
                  </i>
                )}
                preview={preview}
              />
                                }
            {!model.hasDesignatedConnectionToEligibleProducts && model.hasDesignatedConnectionToEligibleProducts != null && (
            <Alert className="emdg-page-alerts emdg-page-alerts--error">
              You are
              {' '}
              <b>not</b>
              {' '}
              eligible to apply for Tier 1.
            </Alert>
            )}
            {model.hasDesignatedConnectionToEligibleProducts && (
            <MultiLineField
              ref={(r) => { this.designatedConnectionToEligibleProducts = r }}
              id="designatedConnEligProductsDescription"
              label={labelDesignatedConnectionDescription}
              value={model.designatedConnectionToEligibleProducts}
              onChange={(designatedConnectionToEligibleProducts) => onChange({ designatedConnectionToEligibleProducts })}
              required={model.hasDesignatedConnectionToEligibleProducts}
              maxLength={1000}
              preview={preview}
            />
            )}
          </>
          )}
        </>
        )}

      </>
    )

    const skeleton = (
      <BooleanRadioButtonField
        id="hasExportReadinessSkeletonTier1"
        label={labelHasExportReadiness}
        dataSource={yesNoDataSource}
        disabled
        value={null}
        onChange={() => { }}
        description={descriptionHasExportReadiness}
      />
    )

    return (
      <div className="form-item">
        <EMDGAccordion expanded header="Tier 1 agreement">
          <BooleanRadioButtonField
            ref={(r) => { this.hasEverExported = r }}
            id="hasEverExportedTier1"
            label="29. Have you ever exported?"
            value={isSelected ? model.hasEverExported : null}
            onChange={(hasEverExported) => onChange({ hasEverExported })}
            required={isSelected}
            dataSource={yesNoDataSource}
            disabled={!isSelected}
            preview={preview}
          />
          {tierSelected ? formElems : skeleton}
        </EMDGAccordion>
      </div>
    )
  }
}
